import { useState, useEffect } from "react";
import {
  Plus,
  Package,
  Trash2,
  Pencil,
  Power,
  ImagePlus,
  Search,
} from "lucide-react";
import Modal from "../../components/ui/Modal";
import { productAPI } from "../../services/api";

const API_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/backend$/, "")
  : "http://localhost:5000";

export default function CompanyMasters() {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = add mode, id = edit mode
  const [form, setForm] = useState({
    code: "",
    name: "",
    min_weight: "",
    max_weight: "",
    material: "PP",
    machine_id: "",
  });
  const [imageFile, setImageFile] = useState(null); // newly picked file, not yet uploaded
  const [imagePreview, setImagePreview] = useState(null); // preview URL (existing image_url or local blob)

  const load = () => {
    setLoading(true);
    productAPI
      .list()
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm({ code: "", name: "", min_weight: "", max_weight: "" });
    setEditingId(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const openAdd = () => {
    resetForm();
    setModal(true);
  };

  const openEdit = (p) => {
    setForm({
      code: p.code || "",
      name: p.name || "",
      min_weight: p.min_weight ?? "",
      max_weight: p.max_weight ?? "",
    });
    setEditingId(p.id);
    setImageFile(null);
    setImagePreview(p.image_url ? `${API_URL}${p.image_url}` : null);
    setModal(true);
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const save = async () => {
    if (!form.code || !form.name || !form.min_weight || !form.max_weight)
      return;
    setSaving(true);
    try {
      let productId = editingId;
      if (editingId) {
        await productAPI.update(editingId, form);
      } else {
        const res = await productAPI.create(form);
        productId = res.data?.id;
      }
      // Image nusta select kela asel tar ithe upload karto (separate multipart request)
      if (imageFile && productId) {
        await productAPI.uploadImage(productId, imageFile);
      }
      resetForm();
      setModal(false);
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this part?")) return;
    await productAPI.delete(id);
    load();
  };

  const toggleStatus = async (p) => {
    const nextStatus = p.status === "Active" ? "Inactive" : "Active";
    try {
      await productAPI.update(p.id, { ...p, status: nextStatus });
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading)
    return <div style={{ padding: 28, color: "var(--text2)" }}>Loading...</div>;

  const filteredProducts = products.filter((p) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (p.code || "").toLowerCase().includes(q) ||
      (p.name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={{ padding: 28 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Part Masters</h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Define part with target weight ranges
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={openAdd}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 8px",
          }}
        >
          <Plus size={18} /> Add Part
        </button>
      </div>

      <div style={{ position: "relative", marginBottom: 18, maxWidth: 360 }}>
        <Search
          size={15}
          color="var(--text3)"
          style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by Part ID or Name..."
          style={{
            width: "100%",
            padding: "8px 12px 8px 34px",
            borderRadius: 8,
            background: "var(--surface2)",
            border: "1px solid var(--border2)",
            color: "var(--text)",
            fontSize: 13,
          }}
        />
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Part ID</th>
              <th>Part Name</th>
              <th>Min Weight</th>
              <th>Max Weight</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((p) => (
              <tr
                key={p.id}
                style={{ opacity: p.status === "Active" ? 1 : 0.6 }}
              >
                <td>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "var(--accent-dim)",
                      border: "1px solid var(--accent)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {p.image_url ? (
                      <img
                        src={`${API_URL}${p.image_url}`}
                        alt={p.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Package size={15} color="var(--accent)" />
                    )}
                  </div>
                </td>
                <td
                  style={{
                    fontSize: 11,
                    color: "var(--text3)",
                    fontFamily: "var(--mono)",
                  }}
                >
                  {p.code}
                </td>
                <td style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</td>
                <td
                  style={{
                    fontFamily: "var(--mono)",
                    color: "var(--danger)",
                    fontWeight: 600,
                  }}
                >
                  {p.min_weight}g
                </td>
                <td
                  style={{
                    fontFamily: "var(--mono)",
                    color: "var(--warn)",
                    fontWeight: 600,
                  }}
                >
                  {p.max_weight}g
                </td>
                <td>
                  <div
                    onClick={() => toggleStatus(p)}
                    role="switch"
                    aria-checked={p.status === "Active"}
                    title={
                      p.status === "Active"
                        ? "Click to deactivate"
                        : "Click to activate"
                    }
                    style={{
                      width: 36,
                      height: 20,
                      borderRadius: 999,
                      background:
                        p.status === "Active" ? "#10e07a" : "var(--surface2)",
                      border: `1px solid ${p.status === "Active" ? "#10e07a" : "var(--border2)"}`,
                      cursor: "pointer",
                      position: "relative",
                      transition: "background 0.2s ease",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: "#fff",
                        position: "absolute",
                        top: 1,
                        left: p.status === "Active" ? 17 : 1,
                        transition: "left 0.2s ease",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                      }}
                    />
                  </div>
                </td>
                <td>
                  <button
                    title="Edit"
                    onClick={() => openEdit(p)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--text4)",
                      padding: 6,
                      borderRadius: 6,
                      display: "flex",
                    }}
                  >
                    <Pencil size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {filteredProducts.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    textAlign: "center",
                    color: "var(--text3)",
                    padding: 20,
                  }}
                >
                  {products.length === 0
                    ? "No part yet. Add one to get started."
                    : `No part match "${searchTerm}".`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modal}
        onClose={() => {
          setModal(false);
          resetForm();
        }}
        title={editingId ? "Edit Part" : "Add Part"}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
          >
            <div className="form-group">
              <label>Part Code *</label>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="PP-005"
              />
            </div>
            <div className="form-group">
              <label>Part Name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Part name"
              />
            </div>
            <div className="form-group">
              <label>Min Weight (g) *</label>
              <input
                type="number"
                value={form.min_weight}
                onChange={(e) =>
                  setForm({ ...form, min_weight: e.target.value })
                }
                placeholder="95"
              />
            </div>
            <div className="form-group">
              <label>Max Weight (g) *</label>
              <input
                type="number"
                value={form.max_weight}
                onChange={(e) =>
                  setForm({ ...form, max_weight: e.target.value })
                }
                placeholder="105"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Part Image</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 8,
                  background: "var(--surface2)",
                  border: "1px solid var(--border2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <ImagePlus size={20} color="var(--text3)" />
                )}
              </div>
              <label
                className="btn-secondary"
                style={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 10px",
                }}
              >
                {imagePreview ? "Change Image" : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={onPickImage}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              marginTop: 4,
            }}
          >
            <button
              className="btn-secondary"
              onClick={() => {
                setModal(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save Changes" : "Add Part"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
